/**
 * @type {Array<PaxCountry>}
 */
const supportedCountries = [
  {
    alpha2: 'AE',
    displayNameEn: 'UAE',
    currencyCode: 'AED',
    defaultLanguage: 'en',
  },
  {
    alpha2: 'AU',
    displayNameEn: 'Australia',
    currencyCode: 'AUD',
    defaultLanguage: 'en',
  },
  {
    alpha2: 'AT',
    displayNameEn: 'Austria',
    currencyCode: 'EUR',
    defaultLanguage: 'de',
  },
  {
    alpha2: 'BE',
    displayNameEn: 'Belgium',
    currencyCode: 'EUR',
    defaultLanguage: 'fr',
  },
  {
    alpha2: 'CA',
    displayNameEn: 'Canada',
    currencyCode: 'CAD',
    defaultLanguage: 'en',
  },
  {
    alpha2: 'DE',
    displayNameEn: 'Germany',
    currencyCode: 'EUR',
    defaultLanguage: 'de',
  },
  {
    alpha2: 'ES',
    displayNameEn: 'Spain',
    currencyCode: 'EUR',
    defaultLanguage: 'es',
  },
  {
    alpha2: 'FI',
    displayNameEn: 'Finland',
    currencyCode: 'EUR',
    defaultLanguage: 'fi',
  },
  {
    alpha2: 'GB',
    displayNameEn: 'UK',
    currencyCode: 'GBP',
    defaultLanguage: 'en',
  },
  {
    alpha2: 'GY',
    displayNameEn: 'Guyana',
    currencyCode: 'GYD',
    defaultLanguage: 'en',
  },
  {
    alpha2: 'HU',
    displayNameEn: 'Hungary',
    currencyCode: 'HUF',
    defaultLanguage: 'hu',
  },
  {
    alpha2: 'IE',
    displayNameEn: 'Ireland',
    currencyCode: 'EUR',
    defaultLanguage: 'en',
  },
  {
    alpha2: 'IT',
    displayNameEn: 'Italy',
    currencyCode: 'EUR',
    defaultLanguage: 'it',
  },
  {
    alpha2: 'NL',
    displayNameEn: 'Netherlands',
    currencyCode: 'EUR',
    defaultLanguage: 'nl',
  },
  {
    alpha2: 'NZ',
    displayNameEn: 'New Zealand',
    currencyCode: 'NZD',
    defaultLanguage: 'en',
  },
  {
    alpha2: 'PT',
    displayNameEn: 'Portugal',
    currencyCode: 'EUR',
    defaultLanguage: 'pt',
  },
  {
    alpha2: 'SE',
    displayNameEn: 'Sweden',
    currencyCode: 'SEK',
    defaultLanguage: 'sv',
  },
  {
    alpha2: 'US',
    displayNameEn: 'USA',
    currencyCode: 'USD',
    defaultLanguage: 'en',
  },
];

/**
 * @type {Array<PaxCurrency>}
 */
const supportedCurrencies = [
  {
    code: 'GBP',
    symbol: '£',
    displayNameEn: 'British Pound',
  },
  {
    code: 'EUR',
    symbol: '€',
    displayNameEn: 'Euro',
  },
  {
    code: 'USD',
    symbol: '$',
    displayNameEn: 'US Dollar',
  },
  {
    code: 'CAD',
    symbol: '$',
    displayNameEn: 'Canadian Dollar',
  },
  {
    code: 'AUD',
    symbol: '$',
    displayNameEn: 'Australian Dollar',
  },
  {
    code: 'SEK',
    symbol: 'kr',
    displayNameEn: 'Swedish Krona',
  },
  {
    code: 'NZD',
    symbol: '$',
    displayNameEn: 'New Zealand Dollar',
  },
  {
    code: 'GYD',
    symbol: '$',
    displayNameEn: 'Guyana Dollar',
  },
  {
    code: 'HUF',
    symbol: 'Ft',
    displayNameEn: 'Hungarian Forint',
  },
  {
    code: 'AED',
    symbol: 'Dhs',
    displayNameEn: 'UAE dirham',
  },
];

/**
 * @type {Array<PaxLanguage>}
 */
const supportedLanguages = [
  {
    code: 'de',
    displayNameEn: 'German',
  },
  {
    code: 'en',
    displayNameEn: 'English',
  },
  {
    code: 'es',
    displayNameEn: 'Spanish',
  },

  {
    code: 'fi',
    displayNameEn: 'Finnish',
  },
  {
    code: 'fr',
    displayNameEn: 'French',
  },
  {
    code: 'hu',
    displayNameEn: 'Hungarian',
  },
  {
    code: 'it',
    displayNameEn: 'Italian',
  },
  {
    code: 'nl',
    displayNameEn: 'Dutch',
  },
  {
    code: 'pt',
    displayNameEn: 'Portuguese',
  },
  {
    code: 'sv',
    displayNameEn: 'Swedish',
  },
];

// --------------------------------------------------------------------------

export class EntityDataHelper {

  /**
   * @return {Array<PaxCountry>}
   */
  static getSupportedCountries() {
    return supportedCountries;
  }

  /**
   * @return {Array<PaxCurrency>}
   */
  static getSupportedCurrencies() {
    return supportedCurrencies;
  }

  /**
   * @return {Array<PaxLanguage>}
   */
  static getSupportedLanguages() {
    return supportedLanguages;
  }

  // --------------------------------------------------------------------------

  /**
   * @param {string} countryCode
   * @return {PaxCountry|undefined}
   */
  static findCountryByCode(countryCode) {
    const codeToFind = countryCode.toUpperCase();

    return supportedCountries.find((country) => {
      return (country.alpha2 === codeToFind);
    });
  }

  /**
   * @param {string} currencyCode
   * @return {PaxCurrency|undefined}
   */
  static findCurrencyModelForCurrencyCode(currencyCode) {
    const codeToFind = currencyCode.toUpperCase();

    return supportedCurrencies.find((currency) => {
      return (currency.code === codeToFind);
    });
  }

  /**
   * @param {string} languageCode
   * @return {PaxLanguage|undefined}
   */
  static findLanguageModelByCode(languageCode) {
    const codeToFind = languageCode.toLowerCase();

    return supportedLanguages.find((lang) => {
      return (lang.code === codeToFind);
    });
  }
}

/**
 * @typedef PaxCountry
 *
 * @property {string} alpha2 - The ISO 2-letter country code
 * @property {string} displayNameEn - The name in English
 * @property {string} currencyCode - The currency code
 * @property {string} defaultLanguage
 *    The default Language used in this country.
 *    Base language code WITHOUT region (e.g. "en", NOT "en-IE").
 *    IMPORTANT: Many countries have more than one language, this
 *    is just the language with the most users in that country.
 */


/**
 * @typedef PaxCurrency
 *
 * @property {string} code - The ISO currency code
 * @property {string} symbol - The symbol for the currency (e.g. $, £, €).
 * @property {string} displayNameEn - The name of the currency in English.
 */

/**
 * @typedef PaxLanguage
 *
 * @property {string} code - The base ISO currency code
 * @property {string} displayNameEn - The name of the language in English.
 */
